// src/components/ui/Toast.tsx
import { brand, fonts } from '@/lib/tokens';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export type ToastItem = {
  id: string;
  variant: ToastVariant;
  message: string;
};

const ICON_FOR_VARIANT: Record<ToastVariant, string> = {
  success: 'check_circle',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

const COLOR_FOR_VARIANT: Record<ToastVariant, { color: string; background: string; border: string }> = {
  success: {
    color: brand.success,
    background: brand.successTint,
    border: 'rgba(74,222,128,0.28)',
  },
  error: {
    color: brand.error,
    background: brand.errorTint,
    border: 'rgba(255,180,171,0.28)',
  },
  warning: {
    color: brand.warning,
    background: brand.warningTint,
    border: 'rgba(251,146,60,0.28)',
  },
  info: {
    color: brand.accent,
    background: brand.accentTint,
    border: 'rgba(104,187,203,0.28)',
  },
};

type Props = {
  toast: ToastItem;
  onDismiss: (id: string) => void;
};

export function Toast({ toast, onDismiss }: Props) {
  const tone = COLOR_FOR_VARIANT[toast.variant];

  return (
    <div
      className="toast-item"
      role={toast.variant === 'error' ? 'alert' : 'status'}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        minWidth: 280,
        maxWidth: 380,
        padding: '12px 14px',
        borderRadius: 10,
        // Was hardcoded rgba(11,11,11,0.92) — always a near-black panel even
        // in light mode (same bug fixed in NotificationBell.tsx's dropdown
        // and ExpandOverlay.tsx's backdrop). globals.css already defines a
        // dedicated, per-theme "glass panel" token for exactly this pattern
        // — use that instead of a one-off opacity value.
        background: 'var(--surface-glass-strong)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: `1px solid ${tone.border}`,
        boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
      }}
    >
      <span
        className="material-symbols-outlined"
        aria-hidden
        style={{ fontSize: 20, color: tone.color, flexShrink: 0, marginTop: 1 }}
      >
        {ICON_FOR_VARIANT[toast.variant]}
      </span>
      <p
        style={{
          flex: 1,
          fontFamily: fonts.sans,
          fontSize: 13,
          lineHeight: 1.5,
          color: brand.onSurface,
          margin: 0,
        }}
      >
        {toast.message}
      </p>
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="toast-dismiss-btn"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 2,
          color: brand.monoDim,
          display: 'inline-flex',
          flexShrink: 0,
        }}
      >
        <span className="material-symbols-outlined" aria-hidden style={{ fontSize: 16 }}>
          close
        </span>
      </button>
    </div>
  );
}
